import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Si hay un WebView en pantalla y dónde puede acoplarse el asistente.
 *
 * Vive en `shared` por el mismo motivo que `active-table.ts`: lo publican componentes de
 * `shared` y de las vistas, y lo lee el asistente. Ninguno de los dos lados debe importar
 * internals del otro.
 *
 * Existe porque el botón flotante del asistente y los flotantes que trae el sitio embebido
 * —los avisos del mapa, su panel de sede— se disputan la misma esquina inferior derecha, y
 * como son documentos distintos ningún `z-index` del iframe puede ganarle al del portal. La
 * solución no es esconder el asistente, sino sacarlo del rectángulo del iframe mientras dure
 * el WebView.
 *
 * Además del WebView, aquí se declaran los huecos donde el asistente puede acoplarse. Son dos:
 * la barra de control de las vistas con mapa y la barra de la tabla dinámica. Cuál usa lo decide
 * el propio asistente (`AssistantLauncher`).
 */

/**
 * Los sitios donde el botón del asistente puede incrustarse.
 *
 * - `controlBar`: la barra de control de las vistas con WebView. Es el único visible mientras
 *   hay un mapa delante, porque la tabla sigue montada pero oculta detrás.
 * - `tableToolbar`: el extremo derecho de la barra de la tabla dinámica, junto al contador de
 *   resultados.
 */
export type AssistantDockKind = 'controlBar' | 'tableToolbar';

interface ActiveWebViewState {
  /** Cuántos paneles hay montados. Es un contador y no un booleano por `StrictMode`: React
   *  monta, desmonta y vuelve a montar, y un booleano se quedaría en `false`. */
  panelCount: number;
  /** Los huecos donde el asistente puede acoplarse, si la vista ofrece alguno. */
  dockNodes: Record<AssistantDockKind, HTMLElement | null>;
  attachPanel: () => void;
  detachPanel: () => void;
  setDockNode: (kind: AssistantDockKind, node: HTMLElement) => void;
  /** Retira el hueco, pero sólo si sigue siendo el que dio de alta quien llama. */
  clearDockNode: (kind: AssistantDockKind, node: HTMLElement) => void;
}

export const useActiveWebViewStore = create<ActiveWebViewState>()((set, get) => ({
  panelCount: 0,
  dockNodes: { controlBar: null, tableToolbar: null },

  attachPanel: () => set((state) => ({ panelCount: state.panelCount + 1 })),

  // El suelo en cero protege de un desmontaje que llegue sin su montaje, que dejaría el
  // contador en negativo y el asistente acoplado para siempre.
  detachPanel: () => set((state) => ({ panelCount: Math.max(0, state.panelCount - 1) })),

  setDockNode: (kind, node) => set((state) => ({ dockNodes: { ...state.dockNodes, [kind]: node } })),

  clearDockNode: (kind, node) => {
    // Al cambiar de ruta, la vista nueva registra su hueco antes de que la vieja retire el
    // suyo. Sin comparar el nodo, la baja de la vieja borraría el hueco de la nueva.
    if (get().dockNodes[kind] === node) {
      set((state) => ({ dockNodes: { ...state.dockNodes, [kind]: null } }));
    }
  },
}));

/** Da de alta un WebView mientras el componente que llama esté montado. */
export function useRegisterWebView(): void {
  useEffect(() => {
    const { attachPanel, detachPanel } = useActiveWebViewStore.getState();
    attachPanel();
    return detachPanel;
  }, []);
}

/** Si ahora mismo se está viendo un sitio embebido. */
export function useIsWebViewActive(): boolean {
  return useActiveWebViewStore((state) => state.panelCount > 0);
}
